import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useState } from 'react';

import { aggridHeaderClass, SmallTitleSpan } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { formatDate } from 'src/utils/string/stringUtil';

import {
	defaultWMS1120_R01ProductResultDto,
	WMS1120_R01ProductResultDto,
} from '../Dto_WMS1120_R01';
import { useWMS1120_R01Store } from '../Store_WMS1120_R01';

const PRODUCT_RESULT_COLUMN_STATE_KEY = 'WMS1120_R01-product-result-grid-column-state';

const applyBottomCenterAlign = <T,>(defs: ColDef<T>[], bottomLabelCols: Set<string>): ColDef<T>[] =>
	defs.map((def) => {
		if (!def?.colId || !bottomLabelCols.has(def.colId)) return def;
		return {
			...def,
			cellStyle: (params: { node?: { rowPinned?: string | null } }) =>
				params?.node?.rowPinned === 'bottom' ? { textAlign: 'center' } : null,
		};
	});

interface Props {
	gridRef: React.RefObject<AgGridReact<WMS1120_R01ProductResultDto> | null>;
	onRowClick?: (data: WMS1120_R01ProductResultDto) => void;
}

export const BottomTableProductResult_WMS1120_R01 = ({ gridRef, onRowClick }: Props) => {
	const {
		WMS1120_R01productResultList,
		selectedWMS1120_R01ProductResult,
		setSelectedWMS1120_R01ProductResult,
		WMS1120_R01productResultColumnState,
		setWMS1120_R01ProductResultColumnState,
		WMS1120ScrollTop,
		setWMS1120ScrollTop,
	} = useWMS1120_R01Store();

	const [, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: 'productResultNo',
		gridData: WMS1120_R01productResultList,
		defaultGridData: {} as WMS1120_R01ProductResultDto,
		selectedRow: undefined,
	});

	const baseColumnDefs: ColDef<WMS1120_R01ProductResultDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '생산실적번호', colId: 'productResultNo', field: 'productResultNo', width: 130,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '합격수량', colId: 'acceptQty', field: 'acceptQty', width: 100,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			valueFormatter: (p) => p.value?.toLocaleString() ?? '0',
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		},
		{
			headerName: '생산일자', colId: 'productDt', field: 'productDt', width: 100,
			valueFormatter: (p) => {
				if (!p.value) return '';
				try {
					return formatDate(new Date(p.value));
				} catch {
					return p.value;
				}
			},
		},
		{
			headerName: '작업자코드', colId: 'productEmp', field: 'productEmp', width: 100,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '작업자명', colId: 'empNm', field: 'empNm', width: 100,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '총생산', colId: 'productQty', field: 'productQty', width: 100,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			valueFormatter: (p) => p.value?.toLocaleString() ?? '0',
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		},
		{
			headerName: '불량수', colId: 'badQty', field: 'badQty', width: 100,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			valueFormatter: (p) => p.value?.toLocaleString() ?? '0',
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		},
	], []);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<WMS1120_R01ProductResultDto>[] = useMemo(() => {
		const merged = WMS1120_R01productResultColumnState === null
			? baseColumnDefs
			: GridCustomColumnDefs<WMS1120_R01ProductResultDto>({
				baseColumnDefs,
				columnState: WMS1120_R01productResultColumnState,
				headerNames: {},
				fallbackHeaderClass: aggridHeaderClass,
			});
		return applyBottomCenterAlign(merged, new Set(['productResultNo']));
	}, [WMS1120_R01productResultColumnState, settingRowIndex, baseColumnDefs]);

	const pinnedBottomRowData = useMemo(() => ([{
		productResultNo: `${WMS1120_R01productResultList.length.toLocaleString()}건`,
		acceptQty: WMS1120_R01productResultList.reduce((sum, item) => sum + (item.acceptQty || 0), 0).toLocaleString(),
		productQty: WMS1120_R01productResultList.reduce((sum, item) => sum + (item.productQty || 0), 0).toLocaleString(),
		badQty: WMS1120_R01productResultList.reduce((sum, item) => sum + (item.badQty || 0), 0).toLocaleString(),
	}]), [WMS1120_R01productResultList]);

	const handleRowClick = useCallback((e: RowClickedEvent<WMS1120_R01ProductResultDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		setSelectedWMS1120_R01ProductResult(rowData);
		onRowClick?.(rowData);
	}, [setSelectedWMS1120_R01ProductResult, onRowClick]);

	return (
		<div className="h-full flex flex-col">
			<SmallTitleSpan>Lot 생산 실적</SmallTitleSpan>
			<div className="flex-1 min-h-0">
				<GridContextWrapper<WMS1120_R01ProductResultDto>
					enableCellCalculation
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					initSelected={() => setSelectedWMS1120_R01ProductResult(undefined)}
					defaultRowData={defaultWMS1120_R01ProductResultDto}
					baseColumnDefs={WMS1120_R01productResultColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setWMS1120_R01ProductResultColumnState}
					localStorageKey={PRODUCT_RESULT_COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Touch<WMS1120_R01ProductResultDto>
						ref={gridRef}
						rowData={paddedGridData}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						getRowId={getRowId}
						rowClassRules={createRowClassRules<WMS1120_R01ProductResultDto>({
							idKey: 'productResultNo',
							selectedList: selectedWMS1120_R01ProductResult?.productResultNo
								? [selectedWMS1120_R01ProductResult.productResultNo]
								: [],
							deletedList: [],
						})}
						onRowClicked={handleRowClick}
						onCellKeyDown={undefined}
						pinnedBottomRowData={pinnedBottomRowData}
						setHoveredRowIndex={setHoveredRowIndex}
						columnStateKey={PRODUCT_RESULT_COLUMN_STATE_KEY}
						setColumnState={setWMS1120_R01ProductResultColumnState}
						scrollTop={WMS1120ScrollTop.productResult}
						setScrollTop={(top) => setWMS1120ScrollTop('productResult', top)}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
